import React, { createContext, useContext, useMemo } from 'react'

import { useGrowthBook } from '@growthbook/growthbook-react'
import { useLocation } from 'react-router-dom'

import {
  CANCEL_OFFER_BASE_ID,
  CANCEL_OFFER_QUESTION_BASE_ID,
  Cohort,
  POST_PAYMENT_PAGES,
  PURCHASE_BASE_ID,
  PageId,
} from '~/shared/constants'
import { getPageIdFromPathName } from '~/shared/helpers/getPageIdFromPathName'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import {
  selectCurrentVariantCohortToUse,
  selectCurrentVariantSteps,
  selectGrowthBookDefaultValues,
} from '~/shared/store/common'
import { selectUserAvailableUpsellPages } from '~/shared/store/user'
import { IStep } from '~/shared/types'

type TPageInfoContextProps = {
  steps: IStep[]
  upsellPageIds: string[]
  activeUpsellPageIds: string[]
  currentPageId: PageId
  currentPageIndex: number
  isSecondPage: boolean
  hasHeader: boolean
  hasProgressbar: boolean
  hasBurgerMenu: boolean
  pagesWithProgressBar: IStep[]
  currentPageNumber: number
  currentSubscriptionPageId: PageId
  cancelOfferPageId: PageId | null
  cancelOfferQuestionPageId: PageId | null
  checkoutPageId: PageId | null
  purchasePageId: PageId | null
  currentUpsellPaywallPageId: PageId
  isCheckoutPage: boolean
  isPurchasePage: boolean
  isCOPage: boolean
  isCOQuestionPage: boolean
  cohortToUse: Cohort
}

const PageInfoContext = createContext<TPageInfoContextProps | null>(null)

const useSelectors = () =>
  useBulkSelector({
    steps: selectCurrentVariantSteps,
    cohortToUse: selectCurrentVariantCohortToUse,
    growthBookDefaultValues: selectGrowthBookDefaultValues,
    availableUpsellPages: selectUserAvailableUpsellPages,
  })

export const PageInfoProvider = ({ children }) => {
  const { pathname } = useLocation()
  const growthBook = useGrowthBook()
  const { steps, cohortToUse, growthBookDefaultValues, availableUpsellPages } =
    useSelectors()

  const unnecessaryPages = (() => {
    if (!growthBook?.ready) return []
    return Object.entries(growthBookDefaultValues).reduce(
      (acc, [key, defaultValues]) => {
        const experimentValue = growthBook.getFeatureValue(
          key,
          JSON.stringify(defaultValues),
        )
        return [...acc, ...JSON.parse(experimentValue)]
      },
      [] as string[],
    )
  })()

  const filteredSteps = unnecessaryPages.length
    ? steps.filter(({ id }) => !unnecessaryPages.includes(id))
    : steps
  const currentPageId = getPageIdFromPathName(pathname)
  const currentPageIndex = filteredSteps.findIndex(
    ({ id }) => id === currentPageId,
  )
  const isSecondPage = currentPageIndex === 1
  const currentPage = filteredSteps[currentPageIndex]
  const isPostPaymentPage = POST_PAYMENT_PAGES.includes(currentPageId)
  const hasHeader = currentPage?.hasHeader || isPostPaymentPage
  const hasProgressbar = currentPage?.hasProgressBar
  const hasBurgerMenu = !currentPageIndex && !currentPage?.isSubscriptions

  const pagesWithProgressBar = filteredSteps.filter(
    ({ hasProgressBar }) => hasProgressBar,
  )

  const currentPageNumber = (() => {
    const pageIndex = pagesWithProgressBar.findIndex(
      ({ id }) => id === currentPageId,
    )

    if (cohortToUse === Cohort.MF_WOMEN_45) {
      const amountOfSkippablePagesBefore = pagesWithProgressBar.reduce(
        (acc, curr, idx) => acc + (idx < pageIndex && curr.isSkippable ? 1 : 0),
        0,
      )

      return pageIndex - amountOfSkippablePagesBefore + 1
    }

    return pageIndex + 1
  })()

  let currentSubscriptionPageId = '' as PageId
  let purchasePageId: PageId | null = null
  let checkoutPageId: PageId | null = null
  let cancelOfferPageId: PageId | null = null
  let cancelOfferQuestionPageId: PageId | null = null

  filteredSteps.forEach(({ isSubscriptions, isPayment, id }) => {
    if (isSubscriptions && !currentSubscriptionPageId) {
      currentSubscriptionPageId = id
    }

    if (isSubscriptions && id.includes(PURCHASE_BASE_ID) && !purchasePageId) {
      purchasePageId = id
    }
    if (isPayment && !checkoutPageId) checkoutPageId = id

    if (
      !cancelOfferPageId &&
      id.includes(CANCEL_OFFER_BASE_ID) &&
      !id.includes(CANCEL_OFFER_QUESTION_BASE_ID)
    ) {
      cancelOfferPageId = id
    }

    if (
      !cancelOfferQuestionPageId &&
      id.includes(CANCEL_OFFER_QUESTION_BASE_ID)
    ) {
      cancelOfferQuestionPageId = id
    }
  })

  const isPurchasePage = purchasePageId === currentPageId
  const isCheckoutPage = checkoutPageId === currentPageId
  const isCOPage = cancelOfferPageId === currentPageId
  const isCOQuestionPage = cancelOfferQuestionPageId === currentPageId

  const upsellPageIds = filteredSteps
    .filter(({ isUpsell }) => isUpsell)
    .map(({ id }) => id)

  const activeUpsellPageIds = availableUpsellPages || upsellPageIds
  const [currentUpsellPaywallPageId] = activeUpsellPageIds

  const value = useMemo(
    () => ({
      steps: filteredSteps,
      upsellPageIds,
      activeUpsellPageIds,
      currentPageId,
      currentPageIndex,
      isSecondPage,
      hasHeader,
      hasProgressbar,
      hasBurgerMenu,
      pagesWithProgressBar,
      currentPageNumber,
      currentSubscriptionPageId,
      cancelOfferPageId,
      purchasePageId,
      cancelOfferQuestionPageId,
      currentUpsellPaywallPageId,
      checkoutPageId,
      isCheckoutPage,
      isPurchasePage,
      isCOPage,
      isCOQuestionPage,
      cohortToUse,
    }),
    [
      filteredSteps,
      upsellPageIds,
      activeUpsellPageIds,
      currentPageId,
      currentPageIndex,
      isSecondPage,
      hasHeader,
      hasProgressbar,
      hasBurgerMenu,
      pagesWithProgressBar,
      currentPageNumber,
      currentSubscriptionPageId,
      checkoutPageId,
      purchasePageId,
      cancelOfferPageId,
      cancelOfferQuestionPageId,
      isCOPage,
      isPurchasePage,
      isCOQuestionPage,
      currentUpsellPaywallPageId,
      isCheckoutPage,
      cohortToUse,
    ],
  )

  return (
    <PageInfoContext.Provider value={value}>
      {children}
    </PageInfoContext.Provider>
  )
}

export const usePageInfo = () => {
  const context = useContext(PageInfoContext)
  if (!context) {
    throw new Error('usePageInfo must be used within a PageInfoProvider')
  }
  return context
}
